import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@loot-os/ui/components/field";
import { ScrollArea } from "@loot-os/ui/components/scroll-area";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { Switch } from "@loot-os/ui/components/switch";
import { useQuery } from "@tanstack/react-query";
import { useId } from "react";

import {
  notificationSettingsQueryOptions,
  useToggleNotificationCategory,
} from "../../lib/notifications.js";
import { useShopper } from "../../lib/shopper.js";

/**
 * One switch per category the store sends, in the store's words. A required category stays on
 * and says so; any other saves the moment it is switched.
 */
export function NotificationSettings() {
  const { client } = useShopper();
  const query = useQuery(notificationSettingsQueryOptions(client));
  const { toggle } = useToggleNotificationCategory();
  const idPrefix = useId();

  return (
    <ScrollArea className="flex min-h-0 flex-1 flex-col [&>[data-slot=scroll-area-viewport]]:min-h-0 [&>[data-slot=scroll-area-viewport]]:flex-1">
      <div className="flex flex-col gap-4 px-4 pt-1 pb-4">
        <p className="text-sm text-muted-foreground">Choose what you hear about.</p>
        {query.isPending ? (
          <div aria-busy="true" className="flex flex-col gap-4">
            <span className="sr-only">Loading settings</span>
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-[18px] w-8 rounded-full" />
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <p role="alert" className="text-sm text-muted-foreground">
            Your settings did not load. Close this and try again.
          </p>
        ) : (
          <FieldGroup className="gap-4">
            {query.data.categories.map((setting) => {
              const id = `${idPrefix}-${setting.category}`;
              return (
                <Field key={setting.category} orientation="horizontal">
                  <FieldContent>
                    <FieldLabel htmlFor={id}>{setting.label}</FieldLabel>
                    {setting.required && <FieldDescription>Always on</FieldDescription>}
                  </FieldContent>
                  <Switch
                    id={id}
                    checked={setting.required || setting.enabled}
                    disabled={setting.required}
                    onCheckedChange={(checked) => toggle(setting.category, checked)}
                  />
                </Field>
              );
            })}
          </FieldGroup>
        )}
      </div>
    </ScrollArea>
  );
}
